import React, { useEffect, useState, useCallback, forwardRef, useMemo } from 'react';

import { Box, DrawerOverlay, Flex, Grid } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { L3_PATHS } from 'Deer/constants/routes';
import { SidePanelButton } from 'components/RabbitOKRSidePanel/Components/SidePanelButton';
import ScrollableSectionNavV2 from 'components/RabbitOKRSidePanel/ScrollableSectionNav';
import { useToast } from 'components/hooks/useToast';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { selectNominatePeers } from 'selectors/nominatePeers';

import {
  AppNavigationOutlined,
  Leaders,
  ManagerScore,
  Peers,
  ReceiverManager1,
  Subordinate,
} from 'assets/assetsV1/iconComponents';

import { PEER_NOMINATION_COHORTS, REVIEW_PEER_STATUS, PEER_TYPE_TAG_NAME_MAP } from 'constants/ProjectConstants';
import { generateKey } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendSelectedNominationEvent } from 'constants/mixpanelHelper';
import { sendClickedOnWithdraw } from 'constants/mixpanelHelpers/reviewsEvents';
import { routes } from 'constants/routes';

import { hideNominatePeers } from 'services/nominatePeers';
import {
  getAllNominations,
  getPeerPool,
  nominatePeer,
  nominateDLM,
  withdrawDLMNomination,
} from 'services/performanceReview';

import { Avatar } from 'uiToolkitV1/Avatar';
import { BottomDrawer, DrawerBody, DrawerContent, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextHeader4 } from 'uiToolkitV1/Typography';

import { NominationSection } from './NominationSection';
import useNominatePeersLinkState from './useNominatePeersLinkState';

const navContainerStyles = {
  gridArea: 'nav',

  display: 'flex',
  flexDirection: 'column',
  gap: '4px',

  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  padding: '4px',
  overflowY: 'auto',
};

const sectionContainerStyles = {
  gridArea: 'content',

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',

  borderRadius: '8px',

  overflowY: 'auto',
  overflowX: 'hidden',
  opacity: 1,
  pointerEvents: 'unset',
};

const CATEGORY_ICON_MAP = {
  JUNIORS: Subordinate,
  SENIORS: Leaders,
  PRIMARY_TEAM: Peers,
  OUTSIDE_TEAM: ManagerScore,
  ALL_ORG: Peers,
  DLM: ReceiverManager1,
};

const GridContainer = styled(Grid)`
  flex: auto 1 1;
  height: 100%;
  grid-template-columns: min-content 1fr;
  grid-template-rows: 1fr min-content;
  grid-template-areas: 'nav content';
  grid-gap: ${({ isHavingSingleItem }) => (isHavingSingleItem ? '0' : '4px')};
`;

const StyledSidePanelButton = styled(SidePanelButton)`
  flex: auto 0 0;
  text-wrap: wrap;
  white-space: break-spaces;
  border-radius: 8px;
`;

export const SEARCH_PLACEHOLDER_TEXT = {
  JUNIORS: 'Search for Direct Team',
  SENIORS: 'Search for Leader',
  PRIMARY_TEAM: 'Search for Team Peer',
  OUTSIDE_TEAM: 'Search for Internal Client',
  ALL_ORG: 'Search for Peer',
  DLM: 'Search for Dotted Line Manager',
};

export const PEER_LIST_ORDER = ['SENIORS', 'JUNIORS', 'OUTSIDE_TEAM', 'PRIMARY_TEAM', 'ALL_ORG', 'DLM'];

const getSource = ({ currentPath }) => {
  const sourcePaths = {
    MyReviewsPendingMe: routes.performanceReview.audit,
    MySummary: routes.home,
    ReviewPanel: routes.performanceReview.manage,
    // deer routes sources
    MyDataReviewManage: L3_PATHS['my-data'].review.myReviews,
    AllReportsReviewManage: L3_PATHS.team.allReports.review.manage,
    DirectReportsReviewManage: L3_PATHS.team.directReports.review.manage,
    CompanyReviewManage: L3_PATHS.company.review.manage,
  };
  let source = '';
  Object.entries(sourcePaths).forEach(([sourceName, sourceRoute]) => {
    const match = matchPath(currentPath, {
      path: sourceRoute,
      exact: true,
    });

    if (match) {
      source = sourceName;
    }
  });
  return source;
};

const calcNominatedStats = ({ nominated = [], DLMNominated = [] }) => {
  const newNominatedStats = {
    JUNIORS: 0,
    SENIORS: 0,
    PRIMARY_TEAM: 0,
    OUTSIDE_TEAM: 0,
    ALL_ORG: 0,
    DLM: 0,
  };
  nominated.forEach((elem) => {
    const key = elem?.peerType;
    newNominatedStats[key] += 1;
  });

  newNominatedStats.DLM = DLMNominated.length;

  return newNominatedStats;
};

const Header = ({ onClose, emp = null }) => (
  <DrawerHeader showCloseButton onClose={onClose}>
    <Flex alignItems="center" p={4}>
      <TextHeader4 bold>Nominate Reviewers For&nbsp;</TextHeader4>
      {emp && (
        <>
          <Avatar src={emp.imageUrl} boxSize="16px" ml={1} />
          <TextHeader4 bold ml={1}>
            {emp.value}
          </TextHeader4>
        </>
      )}
      {!emp && <TextHeader4 bold>You</TextHeader4>}
    </Flex>
  </DrawerHeader>
);

const SectionContent = ({
  key,
  peerRequirement,
  nominatedStats,
  DLMNominated,
  nominatedEmployees,
  empId,
  reviewId,
  nominated,
  exclusions,
  peerPoolsList,
  handleNominatePeer,
  handleRemoveNomination,
}) =>
  forwardRef((props, ref) => (
    <Box w="100%" ref={ref}>
      <NominationSection
        key={key}
        min={peerRequirement[key].minPeers}
        max={peerRequirement[key].maxPeers}
        nominatedCount={nominatedStats[key]}
        placeholderText={PEER_TYPE_TAG_NAME_MAP[key]}
        searchPlaceholderText={SEARCH_PLACEHOLDER_TEXT[key]}
        isActive
        nominatedEmployees={key === PEER_NOMINATION_COHORTS.DLM ? DLMNominated : nominatedEmployees}
        cohortKey={key}
        empId={empId}
        reviewId={reviewId}
        allNominated={[...nominated, ...DLMNominated]}
        exclusions={exclusions}
        recommendedPeers={peerPoolsList}
        nominatePeer={handleNominatePeer}
        isNominationLimitApplicable={key !== PEER_NOMINATION_COHORTS.DLM}
        removeNomination={handleRemoveNomination}
      />
    </Box>
  ));

const LoadingSectionContent = () => (
  <Box p={6}>
    <SkeletonLoader skeletons={['10px', '30px', '10px', '30px', '10px', '30px']} />
  </Box>
);

const NominatePeersSidePanelV1 = () => {
  const [loadingNominatedPeers, setLoadingNominatedPeers] = useState(false);
  const [loadingPeerPool, setLoadingPeerPool] = useState(false);
  const [peerPoolsList, setPeerPoolsList] = useState([]);
  const [peerRequirement, setPeerRequirement] = useState({});
  const [exclusions, setExclusions] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();

  const [nominated, setNominated] = useState([]);
  const [DLMNominated, setDLMNominated] = useState([]);
  const { display, empId, reviewId, onSubmit, emp } = useSelector(selectNominatePeers, shallowEqual);
  const toast = useToast();

  useNominatePeersLinkState({ dispatch, display });

  const nominatedStats = calcNominatedStats({ nominated, DLMNominated });

  const mobileViewProps = isMobileView() ? { marginLeft: '0px', marginRight: '0px' } : {};

  const sortPeerNominationList = (peerListObject) => {
    const sortedPeerListObject = {};
    PEER_LIST_ORDER.forEach((peerType) => {
      if (peerListObject?.[peerType]) {
        sortedPeerListObject[peerType] = peerListObject[peerType];
      }
    });
    return sortedPeerListObject;
  };

  const resetData = () => {
    setNominated([]);
    setLoadingPeerPool(true);
    setPeerPoolsList(null);
    setErrorMessage(null);
  };

  const fetchData = () => {
    if (!reviewId) {
      return;
    }

    resetData();

    getPeerPool({ reviewId, empId })
      .then((res) => {
        if (res) {
          const tempPeerLimits = res.peerLimits;
          const sortedPeerList = tempPeerLimits && sortPeerNominationList(tempPeerLimits);
          setPeerRequirement(sortedPeerList);
          setExclusions(res.exclusions);
          setPeerPoolsList(res.recommendedPeers);

          setLoadingNominatedPeers(true);
          getAllNominations({ reviewId, empId })
            .then((res_) => {
              setNominated(res_.filter((i) => i.peerType !== null));
              setDLMNominated(res_.filter((i) => i.peerType === null));

              setExclusions((prev) => [...prev, ...res_.map((i) => Number(i.peerProfile.employee.id))]);
            })
            .catch((err) => {
              setErrorMessage('Sorry, something went wrong please reload to continue');

              dispatch(hideNominatePeers());
              toast({
                status: 'error',
                title: err.message || 'Some error occurred while fetching nominated peers',
              });
              setErrorMessage('Some error occurred while fetching nominated peers');
            })
            .finally(() => setLoadingNominatedPeers(false));
        } else {
          dispatch(hideNominatePeers());
          toast({
            status: 'error',
            title: 'Some error occurred while fetching the data',
          });
        }
      })
      .catch((err) => {
        dispatch(hideNominatePeers());
        toast({
          status: 'error',
          title: err.message || 'Sorry, something went wrong please reload to continue',
        });
        setErrorMessage('Sorry, something went wrong please reload to continue');
      })
      .finally(() => setLoadingPeerPool(false));
  };

  useEffect(fetchData, [dispatch, empId, reviewId]);

  const handleAddRemoveNomination = useCallback(
    ({ nominated: nominated_ }) => {
      const employees = {
        reviewItems: nominated_,
      };

      return nominatePeer({ reviewId, employees, empId });
    },
    [empId, reviewId]
  );

  const handleAddDLMNomination = useCallback(
    ({ nominatedDLMEmpId }) => nominateDLM({ reviewId, empId, dlmEmpId: nominatedDLMEmpId }),
    [empId, reviewId]
  );

  const handleWithdrawDLMNomination = useCallback(
    ({ nominatedDLMEmpId }) => withdrawDLMNomination({ reviewId, empId, dlmEmpId: nominatedDLMEmpId }),
    [empId, reviewId]
  );

  const handleNominatePeer = useCallback(
    async ({ employee, cohortKey }) => {
      const employeeDetailsObj = {
        peerProfile: {
          employee,
        },
        review: {
          peerStatus: REVIEW_PEER_STATUS.ACCEPTED,
        },
        peerType: cohortKey,
      };

      if (cohortKey === PEER_NOMINATION_COHORTS.DLM) {
        return handleAddDLMNomination({ nominatedDLMEmpId: employee.id })
          .then(() => {
            sendSelectedNominationEvent({
              eventProperties: {
                reviewCycleId: reviewId,
                empId,
                nominatedEmpId: employee.id,
                source: getSource({ currentPath: location.pathname }),
                reviewType: 'DLM',
              },
            });
            toast({ status: 'success', title: 'Nominated' });
            onSubmit();

            setDLMNominated((prevNominated) => [
              ...prevNominated,
              {
                review: {},
                peerProfile: {
                  employee,
                  isSenior: null,
                  isJunior: null,
                  isTeamMember: null,
                  peerPoolTypes: [],
                },
                peerType: null,
                allowReviewFormWithdraw: true,
              },
            ]);
            setExclusions((prev) => [...prev, Number(employee.id)]);
          })
          .catch((err) =>
            toast({
              status: 'error',
              title: err.message || 'Something went wrong!',
            })
          );
      }

      return handleAddRemoveNomination({ nominated: [...nominated, employeeDetailsObj] })
        .then((res) => {
          sendSelectedNominationEvent({
            eventProperties: {
              reviewCycleId: reviewId,
              empId,
              source: 'NominationPanel',

              nominatedEmpId: employee.id,
              panelSource: getSource({ currentPath: location.pathname }),
              reviewType: 'Peer',
            },
          });
          toast({ status: 'success', title: 'Nominated' });
          onSubmit();

          setNominated(res.reviewItems);
          setExclusions((prev) => [...prev, Number(employee.id)]);
        })
        .catch((err) =>
          toast({
            status: 'error',
            title: err.message || 'Something went wrong!',
          })
        );
    },
    [handleAddDLMNomination, handleAddRemoveNomination, nominated, onSubmit, toast]
  );

  const handleRemoveNomination = useCallback(
    ({ details }) => {
      const {
        peerProfile: { employee },
      } = details;

      // case for DML nomination
      if (details.peerType === null) {
        return handleWithdrawDLMNomination({ nominatedDLMEmpId: employee.id })
          .then(() => {
            sendClickedOnWithdraw({
              eventProperties: {
                reviewCycleId: reviewId,
                source: 'NominationPanel',
                empId: employee.id,
                reviewType: 'DLM',
                panelSource: getSource({ currentPath: location.pathname }),
              },
            });
            toast({ status: 'success', title: 'Nomination withdrawn successfully' });

            setDLMNominated((prevNominated) => prevNominated.filter((e) => e.peerProfile.employee.id !== employee.id));
            setExclusions((prev) => [...prev.filter((id) => id !== Number(employee.id))]);
          })
          .catch((err) =>
            toast({
              status: 'error',
              title: err.message || 'Something went wrong!',
            })
          );
      }

      const newNominated = nominated.filter((e) => e.peerProfile.employee.id !== employee.id);

      return handleAddRemoveNomination({ nominated: newNominated })
        .then(() => {
          sendClickedOnWithdraw({
            eventProperties: {
              reviewCycleId: reviewId,
              source: 'NominationPanel',
              empId: employee.id,
              reviewType: 'Peer',
              panelSource: getSource({ currentPath: location.pathname }),
            },
          });
          toast({ status: 'success', title: 'Nomination withdrawn successfully' });
          onSubmit();

          setNominated(newNominated);
          setExclusions((prev) => [...prev.filter((id) => id !== Number(employee.id))]);
        })
        .catch((err) =>
          toast({
            status: 'error',
            title: err.message || 'Something went wrong!',
          })
        );
    },
    [handleAddRemoveNomination, handleWithdrawDLMNomination, nominated, onSubmit, reviewId, toast]
  );

  let tabSections = useMemo(
    () =>
      Object.keys(peerRequirement)
        // .filter((key) =>
        //   showAllOrg || nominatedStats[PEER_NOMINATION_COHORTS.ALL_ORG]
        //     ? true
        //     : key !== PEER_NOMINATION_COHORTS.ALL_ORG
        // )
        .map((key) => {
          const nominatedEmployees = nominated.filter((e) => e.peerType === key);
          // const dlmNominatedEmployees = DLMNominated.filter((emp) => emp.peerType === key);
          return {
            id: `${key}-${generateKey(5)}`, // to trigger re-render
            accessFn: () => true,

            renderNavItem: ({ selected, onClick }) => (
              <StyledSidePanelButton
                onClick={onClick}
                selected={selected}
                Icon={CATEGORY_ICON_MAP[key]}
                notificationCount={
                  peerRequirement[key].minPeers > nominatedStats[key]
                    ? peerRequirement[key].minPeers - nominatedStats[key]
                    : null
                }
              >
                {`${PEER_TYPE_TAG_NAME_MAP[key]}${
                  key === PEER_NOMINATION_COHORTS.DLM ||
                  key === PEER_NOMINATION_COHORTS.PRIMARY_TEAM ||
                  key === PEER_NOMINATION_COHORTS.ALL_ORG
                    ? 's'
                    : ' Peers'
                }`}
              </StyledSidePanelButton>
            ),
            SectionContent: SectionContent({
              key,
              peerRequirement,
              nominatedStats,
              DLMNominated,
              nominatedEmployees,
              empId,
              reviewId,
              nominated,
              exclusions,
              peerPoolsList,
              handleNominatePeer,
              handleRemoveNomination,
            }),
          };
        }),
    [
      DLMNominated,
      empId,
      exclusions,
      handleNominatePeer,
      handleRemoveNomination,
      nominated,
      nominatedStats,
      peerPoolsList,
      peerRequirement,
      reviewId,
    ]
  );

  if (errorMessage) {
    return null;
  }

  if (loadingNominatedPeers || loadingPeerPool || !peerRequirement) {
    tabSections = [
      {
        id: 'Loading',
        accessFn: () => true,
        renderNavItem: ({ selected, onClick }) => (
          <StyledSidePanelButton onClick={onClick} selected={selected} Icon={AppNavigationOutlined}>
            Loading...
          </StyledSidePanelButton>
        ),
        SectionContent: LoadingSectionContent,
      },
    ];
  }

  return (
    <BottomDrawer isOpen={display} onClose={() => dispatch(hideNominatePeers())} autoFocus={false}>
      <DrawerOverlay />
      <DrawerContent {...mobileViewProps}>
        {loadingNominatedPeers ? null : (
          <>
            <Header onClose={() => dispatch(hideNominatePeers())} emp={emp} />
            <DrawerBody>
              <Flex borderRadius="8px" h="100%" bg="surface.EBEFF3" p="1" pb={0} mx={1}>
                <GridContainer isHavingSingleItem={tabSections.length === 1}>
                  <ScrollableSectionNavV2
                    options={tabSections}
                    navContainerStyles={navContainerStyles}
                    sectionContainerStyles={sectionContainerStyles}
                    isHideNavBarWhenSingleSection
                  />
                </GridContainer>
              </Flex>
            </DrawerBody>
          </>
        )}
      </DrawerContent>
    </BottomDrawer>
  );
};

export { NominatePeersSidePanelV1 };
